import { Invite, UserRole } from "../generated/gql-types";
import { getApolloClient } from "../lib/getApolloClient";
import { handleError } from "../util/utils";
import { GET_INVITES_BY_USER, INVITE_USER } from "./invite.gql";
import { CHECK_INVITE_BY_EMAIL } from "./invite.rest";

type InviteInput = {
  email: string;
  role: UserRole;
  clientId: string;
};

/**
 * Error codes returned by the invite check endpoint
 */
export enum InviteCheckErrorCode {
  MISSING_EMAIL = "MISSING_EMAIL",
  MISSING_TENANT_ID = "MISSING_TENANT_ID",
  INVALID_EMAIL = "INVALID_EMAIL",
  TENANT_NOT_FOUND = "TENANT_NOT_FOUND",
  INVITE_NOT_FOUND = "INVITE_NOT_FOUND",
  INVITE_ALREADY_USED = "INVITE_ALREADY_USED",
  INVITE_EXPIRED = "INVITE_EXPIRED",
  INTERNAL_ERROR = "INTERNAL_ERROR",
}

/**
 * Response from the invite check endpoint
 */
export interface InviteCheckResponse {
  success: boolean;
  invite?: {
    id: string;
    email: string;
    userRole: UserRole;
    tenantId: string;
    clientId?: string;
    code: string;
    expiresAt?: string;
  };
  errorCode?: InviteCheckErrorCode;
  message?: string;
}

const INVITE_CHECK_FALLBACK_MESSAGE =
  "Unable to verify invitation. Please try again later.";

const CLIENT_VALIDATION_MESSAGES: Partial<
  Record<InviteCheckErrorCode, string>
> = {
  [InviteCheckErrorCode.MISSING_EMAIL]: "Please enter a valid email address.",
  [InviteCheckErrorCode.MISSING_TENANT_ID]:
    "Organisation not found. Please contact support.",
};

/**
 * Check if an email has a valid pending invite for the current tenant.
 * Used to validate invites before allowing user registration.
 *
 * @param email - The email address to check
 * @param tenantId - Optional tenant ID (defaults to TENANT_CONTEXT env var)
 * @returns InviteCheckResponse with success status and invite details or error
 */
export const checkInviteByEmail = async (
  email: string,
  tenantId?: string
): Promise<InviteCheckResponse> => {
  const resolvedTenantId = tenantId || process.env.TENANT_CONTEXT || "";

  if (!email) {
    return {
      success: false,
      errorCode: InviteCheckErrorCode.MISSING_EMAIL,
      message: CLIENT_VALIDATION_MESSAGES[InviteCheckErrorCode.MISSING_EMAIL],
    };
  }

  if (!resolvedTenantId) {
    return {
      success: false,
      errorCode: InviteCheckErrorCode.MISSING_TENANT_ID,
      message:
        CLIENT_VALIDATION_MESSAGES[InviteCheckErrorCode.MISSING_TENANT_ID],
    };
  }

  try {
    const result = await getApolloClient(true).query({
      query: CHECK_INVITE_BY_EMAIL,
      variables: {
        email: encodeURIComponent(email.toLowerCase().trim()),
        tenantId: resolvedTenantId,
      },
      fetchPolicy: "network-only",
    });

    const data = result.data?.inviteCheck;

    if (data?.success) {
      return {
        success: true,
        invite: data.invite,
      };
    }

    // Handle error responses
    const errorCode =
      (data?.errorCode as InviteCheckErrorCode) ||
      InviteCheckErrorCode.INTERNAL_ERROR;

    return {
      success: false,
      errorCode,
      message: data?.message || INVITE_CHECK_FALLBACK_MESSAGE,
    };
  } catch (error) {
    handleError(error, "[InviteCheck] Error checking invite");
    return {
      success: false,
      errorCode: InviteCheckErrorCode.INTERNAL_ERROR,
      message: INVITE_CHECK_FALLBACK_MESSAGE,
    };
  }
};

export const fetchInvitesByUser = async (userId: string): Promise<Invite[]> => {
  const { data } = await getApolloClient().query({
    query: GET_INVITES_BY_USER,
    variables: { userId },
  });
  return data?.getInvitesSentByUser || [];
};

export const inviteUser = async (invite: InviteInput): Promise<void> => {
  const { data } = await getApolloClient().mutate({
    mutation: INVITE_USER,
    variables: {
      email: invite.email,
      role: invite.role,
      tenantId: process.env.TENANT_CONTEXT,
      clientId: invite.clientId,
    },
  });
  if (data?.inviteUser) {
    console.log("Invite sent successfully");
  }
};
